export function bindPairedScroll(a: HTMLElement, b: HTMLElement): () => void {
  let lock = false;
  const copy = (from: HTMLElement, to: HTMLElement) => {
    if (lock) return;
    lock = true;
    to.scrollTop = from.scrollTop;
    to.scrollLeft = from.scrollLeft;
    lock = false;
  };
  const onA = () => copy(a, b);
  const onB = () => copy(b, a);
  a.addEventListener('scroll', onA, { passive: true });
  b.addEventListener('scroll', onB, { passive: true });
  return () => {
    a.removeEventListener('scroll', onA);
    b.removeEventListener('scroll', onB);
  };
}

function overflowsVertically(el: HTMLElement): boolean {
  return el.scrollHeight > el.clientHeight + 1;
}

function isScrollContainer(el: HTMLElement, view: Window | null): boolean {
  const style = view?.getComputedStyle(el);
  if (!style) return false;
  const oy = style.overflowY;
  return (oy === 'auto' || oy === 'scroll') && overflowsVertically(el);
}

export function isDocumentScroller(doc: Document, el: HTMLElement | null): boolean {
  if (!el) return true;
  return el === doc.scrollingElement || el === doc.documentElement || el === doc.body;
}

/** Prefer an inner overflow:auto pane; otherwise the document scroller. */
export function findIframeScrollRoot(doc: Document): HTMLElement | null {
  const view = doc.defaultView;
  const root = doc.body || doc.documentElement;
  let best: HTMLElement | null = null;
  let bestExtra = 0;
  if (root) {
    root.querySelectorAll<HTMLElement>('*').forEach(el => {
      if (!isScrollContainer(el, view)) return;
      const extra = el.scrollHeight - el.clientHeight;
      if (extra > bestExtra) {
        bestExtra = extra;
        best = el;
      }
    });
  }
  if (best) return best;
  return (doc.scrollingElement || doc.documentElement || doc.body) as HTMLElement | null;
}

function readIframeScroll(win: Window): { x: number; y: number; isDocument: boolean } {
  const el = findIframeScrollRoot(win.document);
  if (el && !isDocumentScroller(win.document, el)) {
    return { x: el.scrollLeft, y: el.scrollTop, isDocument: false };
  }
  return { x: win.scrollX, y: win.scrollY, isDocument: true };
}

function writeIframeScroll(win: Window, x: number, y: number, fromDocument: boolean): void {
  const el = findIframeScrollRoot(win.document);
  if (!fromDocument && el && !isDocumentScroller(win.document, el)) {
    el.scrollLeft = x;
    el.scrollTop = y;
    return;
  }
  win.scrollTo(x, y);
}

/**
 * Public proposal pages scroll the iframe window. Document-level `scroll`
 * fires on `window`, not `documentElement`, so we bind both.
 */
export function bindPairedWindowScroll(a: Window, b: Window): () => void {
  let lock = false;
  const copy = (from: Window, to: Window) => {
    if (lock) return;
    lock = true;
    try {
      const src = readIframeScroll(from);
      writeIframeScroll(to, src.x, src.y, src.isDocument);
    } finally {
      lock = false;
    }
  };
  const onA = () => copy(a, b);
  const onB = () => copy(b, a);
  a.addEventListener('scroll', onA, { passive: true });
  b.addEventListener('scroll', onB, { passive: true });

  const elA = findIframeScrollRoot(a.document);
  const elB = findIframeScrollRoot(b.document);
  const extraA = elA && !isDocumentScroller(a.document, elA) ? elA : null;
  const extraB = elB && !isDocumentScroller(b.document, elB) ? elB : null;
  extraA?.addEventListener('scroll', onA, { passive: true });
  extraB?.addEventListener('scroll', onB, { passive: true });

  return () => {
    a.removeEventListener('scroll', onA);
    b.removeEventListener('scroll', onB);
    extraA?.removeEventListener('scroll', onA);
    extraB?.removeEventListener('scroll', onB);
  };
}

export function bindPairedIframeScroll(
  left: HTMLIFrameElement,
  right: HTMLIFrameElement,
): (() => void) | null {
  try {
    const winA = left.contentWindow;
    const winB = right.contentWindow;
    if (!winA || !winB || !left.contentDocument || !right.contentDocument) return null;
    return bindPairedWindowScroll(winA, winB);
  } catch {
    return null;
  }
}
